import { useMaintenanceInfo } from "@/api/services/global/maintenance";
import { orange, text } from "@/theme/colors";
import { rounded, spacing } from "@/theme/variables";
import { Box, Stack, Typography, styled } from "@mui/material";
import { dayjs } from "@/utils/dayjs";
import { DATE_TIME_FORMAT_WITH_SECONDS } from "@/constants/datetime";

// TopAlert component to display maintenance alerts
export const TopAlert = () => {
  // Fetch maintenance information
  const rs = useMaintenanceInfo();
  const maintenanceInfo = rs?.data?.data;

  // Check if maintenance message should be displayed based on date and time in Tokyo timezone
  const shouldShowMaintenanceMessage = () => {
    if (!maintenanceInfo || !maintenanceInfo.modeStartDateTime || !maintenanceInfo.modeEndDateTime || !maintenanceInfo.maintenanceMessage) {
      return false;
    }

    // Use Tokyo timezone for all dates
    const now = dayjs().tz();
    const startDateTime = dayjs(maintenanceInfo.modeStartDateTime).tz();
    const endDateTime = dayjs(maintenanceInfo.modeEndDateTime).tz();

    // Calculate the effective start time as 09:00:00 of the start date
    const effectiveStartTime = startDateTime.startOf("day").add(9, "hour");

    // Check if current time is within the maintenance window
    return now.isAfter(effectiveStartTime) && now.isBefore(endDateTime);
  };

  // Nothing to show when outside the maintenance window
  if (
    !shouldShowMaintenanceMessage() ||
    !maintenanceInfo?.maintenanceMessage ||
    !maintenanceInfo?.modeStartDateTime ||
    !maintenanceInfo?.modeEndDateTime
  ) {
    return null;
  }

  // Format maintenance message with Tokyo timezone and handle newlines
  const formatMaintenanceMessage = (message: string, startDateTime: string, endDateTime: string) => {
    return String(message ?? "")
      .replace(/%BEGIN_TIME%/g, dayjs(startDateTime).tz().format(DATE_TIME_FORMAT_WITH_SECONDS))
      .replace(/%END_TIME%/g, dayjs(endDateTime).tz().format(DATE_TIME_FORMAT_WITH_SECONDS));
  };

  return (
    // Display maintenance notification
    <MaintenanceAlertCard>
      <Stack gap={1}>
        <Stack direction="row" alignItems="center" gap={1}>
          <AlertIconWrapper>
            <Typography variant="body14Bold" color={text.white} lineHeight={1}>
              !
            </Typography>
          </AlertIconWrapper>
          <Typography color={text.primary} variant="body14Bold">
            サービスメンテナンスのお知らせ
          </Typography>
        </Stack>
        <Typography variant="body14" color={text.secondary} sx={{ pl: "30px", whiteSpace: "pre-line" }}>
          {formatMaintenanceMessage(
            maintenanceInfo.maintenanceMessage,
            maintenanceInfo.modeStartDateTime,
            maintenanceInfo.modeEndDateTime
          )}
        </Typography>
      </Stack>
    </MaintenanceAlertCard>
  );
};

const MaintenanceAlertCard = styled(Box)`
  border: 1px solid #EC7126;
  border-radius: ${rounded.sm};
  background-color: #FFF3E0;
  padding: ${spacing.sm};
  line-height: 150%;
`;

// Orange circle icon wrapper for the "!" icon
const AlertIconWrapper = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  min-width: 20px;
  border-radius: ${rounded.full};
  background-color: ${orange[80]};
`;
